import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1760872646642-5c4cde741cfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBwYXJsaWFtZW50JTIwYnVpbGRpbmclMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lsaW5lfGVufDB8fHx8MTc3ODEzMjgzNHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-navy text-ivory grain"
    >
      <motion.div
        style={{ y }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <img
          src={HERO_IMG}
          alt="Indian institutional architecture at twilight"
          className="w-full h-[115%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/65 to-navy/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(10,17,40,0.85))]" />
      </motion.div>

      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 bg-grid-faint opacity-30"
        style={{ backgroundSize: "80px 80px" }}
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity }}
        className="relative container-bharat min-h-[100svh] flex flex-col justify-end pb-20 md:pb-28 pt-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="w-10 h-px bg-gold" />
          <span className="overline text-gold/90">
            India's Strategic Growth Architecture
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-serif-display text-balance text-5xl sm:text-6xl lg:text-[7.5rem] leading-[0.95] tracking-tight max-w-6xl"
        >
          {"Architecting the".split("").map((c, i) => (
            <motion.span
              key={`a-${i}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 * i }}
              className="inline-block"
            >
              {c === " " ? "\u00A0" : c}
            </motion.span>
          ))}
          <br />
          <motion.span
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0)" }}
            transition={{ duration: 1, delay: 0.9 }}
            className="italic text-gold"
          >
            Next Era
          </motion.span>{" "}
          <motion.span
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="inline-block"
          >
            of Bharat.
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="mt-10 max-w-2xl text-lg md:text-xl text-ivory/75 font-light leading-relaxed"
        >
          Strategic advisory and transformation solutions for the institutions
          shaping India's future — across governance, healthcare, education,
          infrastructure, workforce and Digital Bharat.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.7 }}
          className="mt-12 flex flex-col sm:flex-row gap-5"
        >
          <a
            href="#sectors"
            data-testid="hero-cta-vision"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-ivory text-navy text-sm tracking-wide hover:bg-gold hover:text-navy transition-all duration-300"
          >
            Explore Our Vision
            <ArrowDownRight
              size={16}
              className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </a>
          <a
            href="#engage"
            data-testid="hero-cta-partner"
            className="group inline-flex items-center gap-3 px-7 py-4 border border-ivory/40 text-ivory text-sm tracking-wide hover:bg-ivory hover:text-navy transition-all duration-300"
          >
            Partner With Us
            <span className="w-5 h-px bg-current transition-all group-hover:w-8" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
          className="absolute right-6 md:right-12 bottom-10 hidden md:flex flex-col items-end gap-3"
        >
          <span className="overline text-ivory/60">Estd. 2011 · Pune</span>
          <span className="block w-px h-16 bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
